import {
  QIcon_ai_tutor,
  QIcon_calendar_filled,
  QIcon_chat,
  QIcon_chevron_bold_left,
  QIcon_computer,
  QIcon_home,
  QIcon_person,
} from '@repo/icons/index'
import classNames from 'classnames'
import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import BodyClassName from 'react-body-classname'
import { Slide, ToastContainer, toast as toastType } from 'react-toastify'
import Cookies from 'universal-cookie'
import BasicHead from '../../components/common/BasicHead'
import { useEventTracking } from '../../helpers/gtmHelper'
import { ISeoData } from '../../model/Common/common.interface'
import { usePortalHomeTopBarMenuData } from '../../modules/apis/sanity'
import { useCustomerType } from '../../modules/apis/student/products'
import { logVisitEvent } from '../../modules/braze/logVisitRecord'
import { useCurrentUser, useIsPromotionUser } from '../../modules/context/UserContext'
import useAppRouter from '../../modules/hooks/common/useAppRouter'
import useIsWebview from '../../modules/hooks/common/useIsWebview'
import LinkLocale from '../../modules/i18n/LinkLocale'
import {
  n_ai_tutor,
  n_buy,
  n_common,
  n_event,
  n_guide,
  n_home,
  n_lesson,
  n_myringle,
  n_nav,
} from '../../modules/i18n/strings/auto/wording'
import useTranslation from '../../modules/i18n/useTranslation'
import urls from '../../modules/urls'
import { getDday, isMulticampus } from '../../modules/utils'
import { COLORS, COOKIE_OPT } from '../../modules/vars'
import useGnbStore from '../../store/useGnbStore'
import AvatarImg from '../common/AvatarImg'
import BandBannerContainer from '../common/BandBannerContainer'
import CsrOnlyStudentBrazePixel from '../common/CsrOnlyStudentBrazePixel'
import DeadlineComponent from '../common/DeadlineComponent'
import { IOgData } from '../landing/blog/type'
import LessonFloatingButton from '../new-portal/lessons/LessonFloatingButton'
import AccountMenuBody from '../new-portal/menu/AccountMenuBody'

interface PortalContainerProps {
  ogData?: IOgData
  toast?: boolean
  children?: React.ReactNode
  bgColor?: string
  seoData?: ISeoData
  pageId?: string
  layoutClassName?: string
  noDefaultBar?: boolean
  topBar?: React.ReactNode
  subTopBar?: React.ReactNode
  backTo?: string
  pageName?: string
  eventName?: string
  onBackClick?: () => void
  showBookButton?: boolean
}
interface IMainMenu {
  id: string
  icon: React.ReactNode
  title: string
  href: string
  event: boolean
  none: boolean
}

interface AccountMenuListProps {
  showMenu: boolean
  setShowMenu: Dispatch<SetStateAction<boolean>>
}

export const AccountMenuList = ({ showMenu, setShowMenu }: AccountMenuListProps) => {
  return <AccountMenuBody showMenu={showMenu} setShowMenu={setShowMenu} />
}

export enum PORTAL_MENU {
  MENU_HOME = 'menu_home',
  MENU_LESSON = 'menu_lesson',
  MENU_STUDY = 'menu_study',
  MENU_EVENT = 'menu_event',
  MENU_MY = 'menu_my',
  MENU_NOTICE = 'menu_notice',
  MENU_HELPCENTER = 'menu_helpcenter',
  MENU_FAQ = 'menu_faq',
  MENU_AI_TUTOR = 'menu_ai_tutor',
}

const PortalContainer = ({
  ogData,
  toast,
  children,
  bgColor,
  seoData,
  pageId,
  layoutClassName,
  noDefaultBar,
  topBar,
  subTopBar,
  backTo,
  pageName,
  eventName,
  showBookButton,
  onBackClick,
}: PortalContainerProps) => {
  const { isPromotionUser, dday } = useIsPromotionUser()
  const { currentUser } = useCurrentUser()
  const { t, locale } = useTranslation()
  const router = useAppRouter()
  const [showMenu, setShowMenu] = useState(false)
  const isWebView = useIsWebview()
  const cookies = new Cookies()
  const isSubMenuBar = Boolean(subTopBar)
  const { sendClickEvent } = useEventTracking()
  const { data: customerType } = useCustomerType()
  const isB2CEligible = customerType?.isB2CEligible

  const { data: sanityMenuData } = usePortalHomeTopBarMenuData()

  const topBarMenus = () => {
    if (!sanityMenuData) return []

    const menus = sanityMenuData
      .filter((menu) => menu.isPublic)
      .map((menu) => {
        const dDay = menu?.endDate ? getDday(new Date(menu.endDate)) : null
        const shouldShowDDay = dDay !== null && dDay >= 0 && dDay < 8
        const dDayLabel = shouldShowDDay ? (dDay === 0 ? 'D-Day' : `D-${dDay}`) : null
        return {
          title: menu.label,
          link: menu.link,
          dDayLabel,
        }
      })

    return menus
  }

  const menus: IMainMenu[] = [
    {
      id: PORTAL_MENU.MENU_HOME,
      icon: <QIcon_home width={24} height={24} />,
      title: t(n_home.title),
      href: urls.student.newPortal.home,
      event: false,
      none: false,
    },
    {
      id: PORTAL_MENU.MENU_LESSON,
      icon: <QIcon_computer width={24} height={24} />,
      title: t(n_lesson.title),
      href: urls.student.newPortal.lessons.upcomingLessons,
      event: false,
      none: false,
    },
    {
      id: PORTAL_MENU.MENU_EVENT,
      icon: <QIcon_calendar_filled width={24} height={24} />,
      title: t(n_event.title),
      href: urls.student.newPortal.event.index,
      event: true,
      none: !isB2CEligible,
    },
    {
      id: PORTAL_MENU.MENU_MY,
      icon: <QIcon_person width={24} height={24} />,
      title: t(n_myringle.title),
      href: urls.student.newPortal.myRingle.index,
      event: false,
      none: false,
    },
    {
      id: PORTAL_MENU.MENU_AI_TUTOR,
      icon: <QIcon_ai_tutor width={24} height={24} color={COLORS.GRAY500} />,
      title: t(n_ai_tutor.menu.title),
      href: urls.student.newPortal.aiTutor.index,
      event: false,
      none: false,
    },
  ]

  const bottomMenus = [
    {
      id: PORTAL_MENU.MENU_FAQ,
      selectedIconUrl: 'https://d38emex6h5e12i.cloudfront.net/2024-image/1722846400733_faq-selected.webp',
      unSelectedIconUrl: 'https://d38emex6h5e12i.cloudfront.net/2024-image/1722846410453_faq-unselected.webp',
      hoverIconUrl: 'https://d38emex6h5e12i.cloudfront.net/2024-image/1722846385524_faq-hover.webp',
      title: t(n_common.label_faq),
      href: urls.student.newPortal.helpCenter.index + '#1',
    },
    {
      id: PORTAL_MENU.MENU_HELPCENTER,
      selectedIconUrl: 'https://d38emex6h5e12i.cloudfront.net/2024-image/1722846421492_guide-selected.webp',
      unSelectedIconUrl: 'https://d38emex6h5e12i.cloudfront.net/2024-image/1722846434632_guide-unselected.webp',
      hoverIconUrl: 'https://d38emex6h5e12i.cloudfront.net/2024-image/1722846428273_guide-hover.webp',
      title: t(n_guide.page_title),
      href: urls.student.newPortal.helpCenter.index + '#0',
    },
  ]

  const handleShowMenu = (e) => {
    e.stopPropagation()
    setShowMenu(!showMenu)
  }

  const handleRoute = (menu: IMainMenu) => {
    cookies.set(menu.id, true, COOKIE_OPT)
    router.push(menu.href)
  }

  const handleEventClick = async (url) => {
    sendClickEvent({ button_name: 'portal_top_menu_event' })
    router.push(url)
  }

  const handlePurchase = () => {
    sendClickEvent({ button_name: 'portal_top_menu_pricing' })
    router.push(urls.student.newPortal.pricing.index)
  }

  const handleBusinessPurchase = () => {
    sendClickEvent({ button_name: 'portal_top_menu_business_pricing' })
    router.push(urls.student.newPortal.pricing.business)
  }

  useEffect(() => {
    if (typeof window != 'undefined') {
      window?.ChannelIO('hideChannelButton')
    }
  }, [router.asPath])

  const isGnbShow = useGnbStore((state) => state.isGnbRendered)

  return (
    <>
      <BodyClassName className={`newTheme text-black ${bgColor ? bgColor : null}`} />
      <BasicHead ogData={ogData} seoData={seoData} />

      {pageName && (
        <CsrOnlyStudentBrazePixel
          currentUser={currentUser}
          callback={(appBoy) =>
            logVisitEvent(appBoy, 'view_portal', {
              page_name: pageName,
              locale: locale,
            })
          }
        />
      )}
      {eventName && (
        <CsrOnlyStudentBrazePixel
          currentUser={currentUser}
          callback={(appBoy) =>
            logVisitEvent(appBoy, 'view_event', {
              event_name: eventName,
              locale: locale,
            })
          }
        />
      )}

      {/* Legacy Design System */}
      {toast && (
        <ToastContainer
          style={{ width: '370px', height: '36px', marginBottom: '64px' }}
          toastClassName=" min-h-0 bg-black text-white px-8 text-center h4 text-sBody rounded flex bg-opacity-80"
          position={toastType.POSITION.BOTTOM_CENTER}
          closeButton={false}
          autoClose={3000}
          hideProgressBar
          newestOnTop={false}
          closeOnClick
          rtl={false}
          draggable
          transition={Slide}
          pauseOnHover
          enableMultiContainer
        />
      )}

      <div className="flex bg-white">
        {/* Left Side Menu */}
        <div className="z-200 fixed left-0 hidden h-screen min-w-[80px] max-w-[80px] flex-col items-center bg-[#FBFBFF] px-[8px] py-[20px] lg:flex">
          <div className="px-[8px]">
            <LinkLocale href={urls.student.newPortal.home}>
              <img src="/assets/img/logo/logo1.png" className="h-[34px] w-[34px]" alt="RingleLogo" loading="lazy" />
            </LinkLocale>
          </div>
          <div className="mt-[36px] flex h-full flex-col justify-between gap-y-[4px]">
            <div className="">
              {menus
                .filter((menu: IMainMenu) => !menu.none)
                .map((menu: IMainMenu, i: number) => {
                  return (
                    <div onClick={() => handleRoute(menu)} key={i}>
                      <div
                        className={classNames(
                          'relative flex cursor-pointer flex-col items-center py-[8px] px-[4px] hover:text-black',
                          {
                            'text-purple-500': menu.id === pageId,
                            'text-gray-500': menu.id !== pageId,
                          }
                        )}
                      >
                        <div
                          className={classNames(
                            'flex w-[56px] items-center justify-center px-[12px] py-[4px] hover:rounded-[70px] hover:bg-gray-100',
                            {
                              'rounded-[70px] bg-gray-100': menu.id === pageId,
                              'text-purple-500': menu.id === pageId,
                              'text-gray-500 hover:text-gray-500': menu.id !== pageId,
                            }
                          )}
                        >
                          {menu.icon}
                        </div>
                        <div className={classNames('Body2-14Md')}>{menu.title}</div>
                        {menu.event && !cookies.get(menu.id) && (
                          <div className="absolute top-[8px] right-[24px] h-[6px] w-[6px] rounded-full bg-red-500"></div>
                        )}
                      </div>
                    </div>
                  )
                })}
            </div>

            <div className="flex w-full flex-col text-[14px] font-medium">
              {!isMulticampus(currentUser) && (
                <>
                  {bottomMenus.map((menu, i) => {
                    const isSelected = menu.id === pageId

                    return (
                      <div onClick={() => router.push(menu.href)} key={i}>
                        <div
                          className={classNames(
                            'flex cursor-pointer flex-col items-center py-[8px] px-[12px] hover:text-black',
                            {
                              'text-purple-500': isSelected,
                              'text-gray-500': !isSelected,
                            }
                          )}
                        >
                          <div
                            className={classNames(
                              'flex w-[56px] items-center justify-center px-[12px] py-[4px] hover:rounded-[70px] hover:bg-gray-100',
                              {
                                'rounded-[70px] bg-gray-100': isSelected,
                              }
                            )}
                          >
                            <img
                              src={isSelected ? menu.selectedIconUrl : menu.unSelectedIconUrl}
                              className="w-[24px] h-[24px]"
                              onMouseOver={(e) => (e.currentTarget.src = menu.hoverIconUrl)}
                              onMouseOut={(e) =>
                                (e.currentTarget.src = isSelected ? menu.selectedIconUrl : menu.unSelectedIconUrl)
                              }
                              alt={menu.title}
                            />
                          </div>
                          <div className={classNames('Body2-14Md text-center whitespace-nowrap')}>{menu.title}</div>
                        </div>
                      </div>
                    )
                  })}
                </>
              )}
              <div className="mt-[28px] flex w-full items-center justify-center">
                <div
                  onClick={() => window.ChannelIO?.('showMessenger')}
                  className={classNames(
                    'border-1 flex max-w-[56px] cursor-pointer flex-col items-center rounded-[20px] border-gray-200 p-[12px] hover:bg-gray-100'
                  )}
                >
                  <QIcon_chat color={COLORS.PURPLE400} width={32} height={32} />
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Top Bar */}
        {!noDefaultBar && (
          <div className="z-120 border-b-1 fixed top-0 flex w-full flex-col items-center border-gray-200 bg-white lg:ml-[80px] lg:w-[calc(100%_-_80px)] xl:px-[0px]">
            {/* GNB SECTION */}
            <BandBannerContainer />
            <div className="relative mx-auto flex h-full min-h-[60px] w-full max-w-[1040px] items-center justify-between px-[16px] text-[14px] sm:px-[32px]">
              {onBackClick && (
                <div className="flex cursor-pointer items-center whitespace-nowrap" onClick={() => onBackClick()}>
                  <span>
                    <QIcon_chevron_bold_left width={16} height={16} />
                  </span>
                  <span className="Body2-14Bd ml-[2px] text-gray-700">{t(n_common.btn_back_list)}</span>
                </div>
              )}
              {backTo && (
                <LinkLocale href={backTo}>
                  <div className="flex items-center whitespace-nowrap">
                    <span>
                      <QIcon_chevron_bold_left width={16} height={16} />
                    </span>
                    <span className="Body2-14Bd ml-[2px] text-gray-700">{t(n_common.btn_back_list)}</span>
                  </div>
                </LinkLocale>
              )}
              {!isWebView && (
                <div className="flex h-full w-full flex-row items-center justify-end gap-x-[20px] text-[14px]">
                  {!isMulticampus(currentUser) && (
                    <>
                      {isB2CEligible &&
                        topBarMenus().map((menu, i) => (
                          <button key={i} onClick={() => handleEventClick(menu.link)}>
                            <div className="flex cursor-pointer items-center">
                              <span className="mx-[6px]">{t(menu.title)}</span>
                              {menu.dDayLabel && (
                                <div className="Caption-12Bd inline-block rounded-[4px] bg-red-500 px-[6px] py-[2px]">
                                  <span className="whitespace-nowrap text-white">{menu.dDayLabel}</span>
                                </div>
                              )}
                            </div>
                          </button>
                        ))}
                      {isB2CEligible && (
                        <button onClick={handlePurchase}>
                          <div className="flex cursor-pointer items-center">
                            <span className="mx-[6px]">{t(n_buy.title)}</span>
                            {isPromotionUser && (
                              <div className="Caption-12Bd inline-block rounded-[4px] bg-red-500 px-[6px] py-[2px]">
                                <span className="whitespace-nowrap text-white">
                                  <DeadlineComponent dday={dday} />
                                </span>
                              </div>
                            )}
                          </div>
                        </button>
                      )}
                      <button onClick={handleBusinessPurchase}>
                        <div className="flex cursor-pointer items-center">
                          <span className="mx-[6px]">{t(n_nav.button.go_to_b2b2c)}</span>
                        </div>
                      </button>
                      <div className="h-[20px] w-[1px] bg-gray-200"></div>
                    </>
                  )}
                  <div className="relative flex cursor-pointer items-center" onClick={handleShowMenu}>
                    <AvatarImg size={33} src={currentUser?.image_url} className="border-[1.5px] border-purple-300" />
                  </div>
                </div>
              )}
              <AccountMenuList showMenu={showMenu} setShowMenu={setShowMenu} />
            </div>
          </div>
        )}

        {/*  Top Bar */}
        {topBar && (
          <div className="z-100 fixed flex w-full flex-col items-center border-gray-200 bg-white lg:ml-[80px] lg:flex lg:w-[calc(100%_-_80px)]">
            {/* GNB SECTION */}
            <BandBannerContainer />
            {topBar}
          </div>
        )}
        {/* Sub Top Bar */}
        {subTopBar && (
          <div className="z-60 fixed top-[60px] flex-col items-center border-gray-200 bg-white sm:flex sm:w-full lg:ml-[80px] lg:flex lg:w-[calc(100%_-_80px)]">
            {subTopBar}
          </div>
        )}
        <div className="min-w-[0px] lg:min-w-[80px]"></div>
        <div
          className={classNames(
            layoutClassName ? layoutClassName : 'mx-auto mb-[120px] w-full max-w-[1040px]',
            isSubMenuBar ? 'mt-[103px]' : 'mt-[52px]'
          )}
        >
          {isGnbShow && <div className="h-[0px] sm:h-[48px]"></div>}
          {children}
        </div>
        {showBookButton && (
          <div className="pointer-events-none fixed bottom-[40px] flex w-full flex-col items-center border-gray-200 px-[16px] sm:px-[32px] lg:ml-[80px] lg:w-[calc(100%_-_80px)] xl:px-[0px] z-50">
            <div className="relative mx-auto flex w-full max-w-[1040px] items-center justify-end">
              <LessonFloatingButton />
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default PortalContainer
